import { getCompanyName } from "@lib/utilities/Constants";
import {
  ABOUT_ICON,
  BLOG_ICON,
  DELETE_ICON,
  FAQ_ICON,
  PRIVACY_ICON,
  RESET_ICON,
  SIGNOUT_CUSTOM_ICON,
  TERMS_ICON,
} from "./IMAGES";
import { typtapFaqData } from "./faq-data/typtapFaq";
import { hcpciFaqData } from "./faq-data/hcpciFaq";
import { tailrowFaqData } from "./faq-data/tailrowFaq";
import { praxisFaqData } from "./faq-data/praxisFaq";
import { coreFaqData } from "./faq-data/coreFaq";

interface MessagesType {
  error: {
    resetPasswordLogout: string;
    inDev: string;
    paymentsError: string;
    noInternet: string;
    noData: string;
    somthingWrong: string;
    noHarmonyUserFound: string;
    documentDownloadFailed: string;
    downloadFailedTryAgain: string;
    trycatchError: string;
    networkError: string;
    noError: string;
    userFound: string;
    noUserFound: string;
    noPolicyFound: string;
    noDataFound: string;
    youAreOffline: string;
    thereIsNoData: string;
    thereAreNoPoliciesAssociated: string;
    registrationFailed: string;
    errorInGrantingAccess: string;
    noNotification: string;
    introspectionActiveTrue: string;
    introspectionActiveFail: string;
    callAgainAPI: string;
    logout: string;
    sessionExpired: string;
    pleaseLoginAgain: string;
    currentPasswordDidNotMatch: string;
    noNotificationFound: string;
    noNotificationSubMsg: string;
    deleteAccountMessage: string;
    badRequest: string;
    invalidData: string;
    validData: string;
    badURL: string;
    pdfError: string;
    featureNotAvailable: string;
  };
  validation: {
    firstNameRequired: string;
    lastNameRequired: string;
    passcodeRequired: string;
    phoneRequired: string;
    emailRequired: string;
    invalidNumber: string;
    emailAlreadyExists: string;
    wrongEmailFormat: string;
    invalidPasscodeFormat: string;
    passcodeLengthMessage: string;
    passcodeLengthGreaterThan20: string;
    codeFormat: string;
    codeMismatch: string;
    emailHint: string;
  };
  success: {
    resetPasswordSuccess: string;
    accountCreated: string;
    downloadSuccess: string;
  };
  payments: {
    payOnline: string;
    paymentOptions: { type: string; title: string; desc: string }[];
    confirmation: string;
    maintenance: string;
  };
}

interface LabelTextType {
  button: {
    login: string;
    signup: string;
    privacy: string;
    terms: string;
    manageAccount: string;
    resetPassword: string;
    deleteAccount: string;
    signOut: string;
    submit: string;
    billing: string;
    docs: string;
    claims: string;
    agent: string;
    continue: string;
  };
  login: {
    dontHaveAccount: string;
    privacy: string;
    terms: string;
  };
  policies: {
    choosePolicy: string;
    effectiveDate: string;
  };
  actions: {
    docsAndInfo: string;
    makeClaim: string;
    billingAndPayments: string;
    contactAgent: string;
    docsAndInfoNoCaps: string;
    makeClaimNoCaps: string;
    billingAndPaymentsNoCaps: string;
    contactAgentNoCaps: string;
  };
  billing: {
    noPaymentsDue: string;
  }; // explicitly empty
  claim: {
    heading1: string;
  };
  docsInfo: {}; // explicitly empty
  contactAgent: {
    findUs: string;
    mailUs: string;
    callUs: string;
    yourAgent: string;
    contactMyAgent: string;
  };
  reset: {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
    resetPassword: string;
  };
  notifications: {
    loadingNotifications: string;
    noNotification: string;
    noNotificationSubMsg: string;
  };
  loading: {
    fetchingYourPolicy: string;
    loadingDocument: string;
    downloadingDocument: string;
    loadingPolicyDetails: string;
    loadingNotifications: string;
  };
  delete: {
    heading: string;
  };
  commonLabels: {
    callUs: string;
    emailUs: string;
    faxUs: string;
    forgotPassword: string;
  };
}

interface commonStringTypes {
  messages: MessagesType;
  labelText: LabelTextType;
}

const commonStrings: commonStringTypes = {
  messages: {
    error: {
      resetPasswordLogout: "Please login again with your updated credentials",
      inDev: "Reset Password API is in development",
      noInternet: "Please check your internet connection and try again",
      noData: "No data available.",
      paymentsError: "Unable to initiate payment. \nPlease try again later.",
      somthingWrong: "Something went wrong",
      noHarmonyUserFound: "Something went wrong",
      documentDownloadFailed: "Document download failed",
      downloadFailedTryAgain: "Download failed. Please try again",
      trycatchError: "Something went wrong",
      networkError: "Network Error",
      noError: "",
      userFound: "User found",
      noUserFound: "No such user",
      noPolicyFound: "No policies found",
      noDataFound: "No data found",
      youAreOffline: "You are offline",
      thereIsNoData: "We are unable to show the details for this policy",
      thereAreNoPoliciesAssociated:
        "We could not find any policy associated with this email.",
      registrationFailed: "Registration failed",
      errorInGrantingAccess: "Error in granting access",
      noNotification: "No Notifications",
      introspectionActiveTrue: "Introspection Active True",
      introspectionActiveFail: "Introspection Active Fail",
      callAgainAPI: "Call again api",
      logout: "Logout",
      sessionExpired: "Session Expired",
      pleaseLoginAgain: "Please login again to continue",
      currentPasswordDidNotMatch: "Current Password did not match",
      noNotificationFound: "No Notifications",
      noNotificationSubMsg: "We will notify you once we have something for you",
      deleteAccountMessage: "Account deleted. Sorry to see you go!",
      badRequest: "BAD_REQUEST",
      invalidData: "Invalid data",
      validData: "Valid data",
      badURL: "Don't know how to open this URL:",
      pdfError: "Unable to load",
      featureNotAvailable: "This fetaure is not available.",
    },
    validation: {
      firstNameRequired: "First Name is required",
      lastNameRequired: "Last Name is required",
      passcodeRequired: "Required",
      phoneRequired: "Phone Number is required",
      emailRequired: "Email is required",
      invalidNumber: "Invalid Phone Number",
      emailAlreadyExists:
        "We couldn't create your account with this email address.",
      wrongEmailFormat: "Email is invalid",
      invalidPasscodeFormat: "Password does not meet the required criteria",
      passcodeLengthMessage:
        "Password length must be between 8 - 20 characters",
      passcodeLengthGreaterThan20: "",
      codeFormat:
        "Must contain minimum 8 - 20 characters, upper and lowercase letters, special character and at least one number.",
      codeMismatch: "The passwords you entered do not match",
      emailHint: "Please enter the email already associated to your policy",
    },
    success: {
      resetPasswordSuccess: "Your password has been reset successfully.",
      accountCreated: "ACCOUNT CREATED",
      downloadSuccess: "Document downloaded successfully",
    },
    payments: {
      maintenance:
        "Payments are temporarily unavailable. Please check back later.",
      payOnline: "Pay Online",
      paymentOptions: [
        {
          type: "card",
          title: "Credit or Debit Card",
          desc: "A 2.99% non-refundable service fee will apply to all credit or debit card payments. This fee is collected by a third-party payment processor.",
        },
        {
          type: "bank",
          title: "Bank Transfer (ACH)",
          desc: "No service fee - a convenient and cost-free way to pay online.",
        },
        {
          type: "mail",
          title: "Check or Money Order by Mail",
          desc: "No service fee - traditional methods remain available.",
        },
      ],
      confirmation:
        "By clicking ‘Continue’, you acknowledge that you are being redirected to a third-party payment processor to complete your transaction.",
    },
  },
  labelText: {
    button: {
      login: "Login",
      signup: "Sign up",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      manageAccount: "Manage Account",
      resetPassword: "Reset Password",
      deleteAccount: "Delete Account",
      signOut: "Sign out",
      submit: "Submit",
      billing: "Billing",
      docs: "Docs",
      claims: "Claims",
      agent: "Agent",
      continue: "Continue",
    },
    login: {
      dontHaveAccount: "Don't have an account?",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
    },
    policies: {
      choosePolicy: "CHOOSE A POLICY",
      effectiveDate: "Effective Date",
    },
    actions: {
      docsAndInfo: "DOCS & INFO",
      makeClaim: "REPORT A CLAIM",
      billingAndPayments: "BILLING & PAYMENTS",
      contactAgent: "CONTACT MY AGENT",
      docsAndInfoNoCaps: "Docs & Info",
      makeClaimNoCaps: "Report a Claim",
      billingAndPaymentsNoCaps: "Billings & Payments",
      contactAgentNoCaps: "Contact my Agent",
    },
    billing: {
      noPaymentsDue: "No payment due for this policy.",
    },
    claim: {
      heading1: "REPORT A CLAIM",
    },
    docsInfo: {},
    contactAgent: {
      findUs: "Find Us",
      mailUs: "Mail Us",
      callUs: "Call Us",
      yourAgent: "YOUR AGENT",
      contactMyAgent: "CONTACT MY AGENT",
    },
    reset: {
      currentPassword: "Current Password",
      newPassword: "New Password",
      confirmNewPassword: "Confirm New Password",
      resetPassword: "RESET PASSWORD",
    },
    notifications: {
      loadingNotifications: "Loading Notifications",
      noNotification: "No Notifications",
      noNotificationSubMsg: "We will notify you once we have something for you",
    },
    loading: {
      fetchingYourPolicy: "Fetching your policies, please wait",
      loadingDocument: "Loading document, please wait",
      downloadingDocument: "Downloading document, please wait",
      loadingPolicyDetails: "Loading policy details",
      loadingNotifications: "Loading Notifications",
    },
    delete: {
      heading: "Once you delete your account:",
    },

    commonLabels: {
      callUs: "Call Us",
      emailUs: "Email Us",
      faxUs: "Fax Us",
      forgotPassword: "Forgot Password?",
    },
  },
};

interface DescriptionAction {
  description1: string;
  description2: string;
}

interface DescriptionClaim {
  description1: string;
}

interface DescriptionContactAgent {
  description1: string;
}

interface DescriptionReset {
  description1: string;
  description2: string;
  description3: string;
}

interface DescriptionDelete {
  heading: string;
  description1: string;
  description2: string;
  description3: string;
  description4: string;
  description5: string;
}

interface DescriptionForgotPassword {
  description1: string;
}

interface Description {
  action: DescriptionAction;
  billing: Object;
  claim: DescriptionClaim;
  docsInfo: Object;
  contactAgent: DescriptionContactAgent;
  reset: DescriptionReset;
  delete: DescriptionDelete;
  forgotPassword: DescriptionForgotPassword;
}

interface Urls {
  privacyURL: string;
  termsURL: string;
  mapURL: string;
  aboutURL?: string | null;
  faqURL?: string | null;
  blogURL?: string | null;
}

interface Brand {
  messages: typeof commonStrings.messages;
  labelText: typeof commonStrings.labelText;
  description: Description;
  urls: Urls;
  drawerData: any[];
}

interface BrandStringTypes {
  [key: string]: Brand;
}
interface ContactInfo {
  phoneNumber: string;
  email: string;
  fax: string;
  customerCareEmail: string;
  customerCarePhone: string;
}

interface BrandContactsType {
  [key: string]: ContactInfo;
}

const BrandStrings: BrandStringTypes = {
  TYPTAP: {
    messages: commonStrings.messages,
    labelText: commonStrings.labelText,
    description: {
      action: {
        description1:
          "TypTap Insurance set out to change how you talk to insurance companies.",
        description2:
          "We understand protecting your home is really important. It’s important to us, too.",
      },
      billing: {},
      claim: {
        description1:
          "If the worst happens, TypTap has your back. We have 24/7 claims reporting with a dedicated team to walk you through the process, from start to finish.",
      },
      docsInfo: {},
      contactAgent: {
        description1:
          "If you have questions regarding your policy or need to make updates, contact your Agent of Record for assistance.",
      },
      reset: {
        description1:
          "Must contain minimum 8 - 20 characters, upper and lowercase letters, special character and at least one number.",
        description2: "We have sent a verification email to",
        description3: "Please verify your email address.",
      },
      delete: {
        heading: "Once you delete your account:",
        description1:
          "You won't be able to access your policies through TypTap app on any platform.",
        description2:
          "You will not receive any updates on your device regarding your policies from TypTap app.",
        description3:
          "We will delete your personal data, such as your full name, email address and phone number from TypTap account services.",
        description4:
          "All your policy details such as claims, coverage details will remain in TypTap.",
        description5:
          "You will immediately lose access to TypTap app across all the platforms. Are you sure you want to delete your account?",
      },
      forgotPassword: {
        description1: "We will send an email to ",
      },
    },
    urls: {
      privacyURL: "https://typtap.com/privacy",
      termsURL: "https://typtap.com/terms",
      mapURL: "https://www.google.com/maps/search/?api=1&query=",
      aboutURL: null,
      faqURL: null,
      blogURL: null,
    },
    drawerData: [],
  },
  HCPCI: {
    messages: commonStrings.messages,
    labelText: {
      ...commonStrings.labelText,
      actions: {
        ...commonStrings.labelText.actions,
        makeClaim: "REPORT A CLAIM",
      },
      claim: {
        ...commonStrings.labelText.claim,
        heading1: "REPORT A CLAIM",
      },
    },
    description: {
      action: {
        description1: "We’re here when it matters most.",
        description2:
          "Providing responsive and expert service. Thank you for placing your trust in us.",
      },
      billing: {},
      claim: {
        description1:
          "If the worst happens, we have your back. You can have peace of mind knowing our dedicated claims team is here to assist you through the claims process from start to finish.",
      },
      docsInfo: {},
      contactAgent: {
        description1:
          "For any questions about your policy or to request changes, please reach out to your insurance agent for assistance.",
      },
      reset: {
        description1:
          "Must contain minimum 8 - 20 characters, upper and lowercase letters, special character and at least one number.",
        description2: "We have sent a verification email to",
        description3: "Please verify your email address.",
      },
      delete: {
        heading: "Once you delete your account:",
        description1:
          "You won't be able to access your policies through Homeowners Choice app on any platform.",
        description2:
          "You will not receive any updates on your device regarding your policies from Homeowners Choice app.",
        description3:
          "We will delete your personal data, such as your full name, email address and phone number from Homeowners Choice account services.",
        description4:
          "All your policy details such as claims, coverage details will remain in Homeowners Choice.",
        description5:
          "You will immediately lose access to Homeowners Choice app across all the platforms. Are you sure you want to delete your account?",
      },
      forgotPassword: {
        description1: "We will send an email to ",
      },
    },
    urls: {
      privacyURL: "https://hcpci.com/privacy-policy",
      termsURL: "https://hcpci.com/terms-of-use/",
      mapURL: "https://www.google.com/maps/search/?api=1&query=",
      aboutURL: null,
      faqURL: null,
      blogURL: null,
    },
    drawerData: [],
  },
  TAILROW: {
    messages: commonStrings.messages,
    labelText: {
      ...commonStrings.labelText,
      actions: {
        ...commonStrings.labelText.actions,
        makeClaim: "REPORT A CLAIM",
      },
      claim: {
        ...commonStrings.labelText.claim,
        heading1: "REPORT A CLAIM",
      },
    },
    description: {
      action: {
        description1: "Protecting What Matters Most.",
        description2:
          "From unexpected storms to everyday risks, Tailrow is here when you need us most.",
      },
      billing: {},
      claim: {
        description1:
          "If the worst happens, we have your back. You can have peace of mind knowing our dedicated claims team is here to assist you through the claims process from start to finish.",
      },
      docsInfo: {},
      contactAgent: {
        description1:
          "Contact your Agent of Record for policy questions or coverage updates.",
      },
      reset: {
        description1:
          "Must contain minimum 8 - 20 characters, upper and lowercase letters, special character and at least one number.",
        description2: "We have sent a verification email to",
        description3: "Please verify your email address.",
      },
      delete: {
        heading: "Once you delete your account:",
        description1:
          "You won't be able to access your policies through Tailrow app on any platform.",
        description2:
          "You will not receive any updates on your device regarding your policies from Tailrow app.",
        description3:
          "We will delete your personal data, such as your full name, email address and phone number from Tailrow account services.",
        description4:
          "All your policy details such as claims, coverage details will remain in Tailrow.",
        description5:
          "You will immediately lose access to Tailrow app across all the platforms. Are you sure you want to delete your account?",
      },
      forgotPassword: {
        description1: "We will send an email to ",
      },
    },
    urls: {
      privacyURL: "https://tailrow.com/privacy_policy",
      termsURL: "https://tailrow.com/terms_of_service",
      mapURL: "https://www.google.com/maps/search/?api=1&query=",
      aboutURL: null,
      faqURL: null,
      blogURL: null,
    },
    drawerData: [],
  },
  PRAXIS: {
    messages: commonStrings.messages,
    labelText: commonStrings.labelText,
    description: {
      action: {
        description1: "Protecting the place where life's best moments happen",
        description2:
          "From the unexpected to the everyday, Praxis is with you.",
      },
      billing: {},
      claim: {
        description1:
          "In times of crisis, we’re here for you. Count on our team to go the extra mile for smooth claims support and timely indemnity.",
      },
      docsInfo: {},
      contactAgent: {
        description1:
          "Contact your Agent of Record for policy questions or coverage updates.",
      },
      reset: {
        description1:
          "Must contain minimum 8 - 20 characters, upper and lowercase letters, special character and at least one number.",
        description2: "We have sent a verification email to",
        description3: "Please verify your email address.",
      },
      delete: {
        heading: "Once you delete your account:",
        description1:
          "You won't be able to access your policies through Praxis app on any platform.",
        description2:
          "You will not receive any updates on your device regarding your policies from Praxis app.",
        description3:
          "We will delete your personal data, such as your full name, email address and phone number from Praxis account services.",
        description4:
          "All your policy details such as claims, coverage details will remain in Praxis.",
        description5:
          "You will immediately lose access to Praxis app across all the platforms. Are you sure you want to delete your account?",
      },
      forgotPassword: {
        description1: "We will send an email to ",
      },
    },
    urls: {
      privacyURL: "https://www.praxisinsures.com/privacy",
      termsURL: "https://www.praxisinsures.com/terms",
      mapURL: "https://www.google.com/maps/search/?api=1&query=",
      aboutURL: "https://www.praxisinsures.com/about",
      faqURL: "https://www.praxisinsures.com/faq",
      blogURL: "https://www.praxisinsures.com/blog",
    },
    drawerData: [
      {
        title: "Manage Profile",
        data: [
          {
            id: "resetPassword",
            label: "Reset Password",
            icon: RESET_ICON,
          },
          {
            id: "deleteAccount",
            label: "Delete Account",
            icon: DELETE_ICON,
          },
        ],
      },
      {
        title: "Help & Resources",
        data: [
          {
            id: "about",
            label: "About Praxis",
            icon: ABOUT_ICON,
          },
          {
            id: "faq",
            label: "FAQs",
            icon: FAQ_ICON,
          },
          {
            id: "blog",
            label: "Blog",
            icon: BLOG_ICON,
          },
          {
            id: "privacy",
            label: "Privacy Policy",
            icon: PRIVACY_ICON,
          },
          {
            id: "terms",
            label: "Terms of Service",
            icon: TERMS_ICON,
          },
        ],
      },
      {
        title: "",
        data: [
          {
            id: "signout",
            label: "Sign Out",
            icon: SIGNOUT_CUSTOM_ICON,
          },
        ],
      },
    ],
  },
  CORE: {
    messages: commonStrings.messages,
    labelText: commonStrings.labelText,
    description: {
      action: {
        description1: "Protecting what matters most.",
        description2:
          "From every storm to every sunrise, CORE is at the center of your protection.",
      },
      billing: {},
      claim: {
        description1:
          "At the CORE of every claim is our commitment to you. Count on us for local expertise, timely indemnity, and support that goes the extra mile.",
      },
      docsInfo: {},
      contactAgent: {
        description1:
          "Contact your Agent of Record for policy questions or coverage updates.",
      },
      reset: {
        description1:
          "Must contain minimum 8 - 20 characters, upper and lowercase letters, special character and at least one number.",
        description2: "We have sent a verification email to",
        description3: "Please verify your email address.",
      },
      delete: {
        heading: "Once you delete your account:",
        description1:
          "You won't be able to access your policies through CORE app on any platform.",
        description2:
          "You will not receive any updates on your device regarding your policies from CORE app.",
        description3:
          "We will delete your personal data, such as your full name, email address and phone number from CORE account services.",
        description4:
          "All your policy details such as claims, coverage details will remain in CORE.",
        description5:
          "You will immediately lose access to CORE app across all the platforms. Are you sure you want to delete your account?",
      },
      forgotPassword: {
        description1: "We will send an email to ",
      },
    },
    urls: {
      privacyURL: "https://coreins.com/privacy-policy/",
      termsURL: "https://coreins.com/terms-of-use/",
      mapURL: "https://www.google.com/maps/search/?api=1&query=",
      aboutURL: "https://coreins.com/",
      faqURL:// null,
      "https://coreins.com/faq/",
      blogURL: "",
    },
    drawerData: [
      {
        id:1,
        title: "Manage Profile",
        data: [
          {
            id: "resetPassword",
            label: "Reset Password",
            icon: RESET_ICON,
          },
          {
            id: "deleteAccount",
            label: "Delete Account",
            icon: DELETE_ICON,
          },
        ],
      },
      {
        id:2,
        title: "Help & Resources",
        data: [
          {
            id: "about",
            label: "About CORE",
            icon: ABOUT_ICON,
          },
          {
            id: "faq",
            label: "FAQs",
            icon: FAQ_ICON,
          },
          {
            id: "privacy",
            label: "Privacy Policy",
            icon: PRIVACY_ICON,
          },
          {
            id: "terms",
            label: "Terms of Service",
            icon: TERMS_ICON,
          },
        ],
      },
      {
        id:3,
        title: "",
        data: [
          {
            id: "signout",
            label: "Sign Out",
            icon: SIGNOUT_CUSTOM_ICON,
          },
        ],
      },
    ],
  },
};

const FaqJsonData = {
  TYPTAP: typtapFaqData,
  HCPCI: hcpciFaqData,
  TAILROW: tailrowFaqData,
  PRAXIS: praxisFaqData,
  CORE: coreFaqData
}

const BrandContacts: BrandContactsType = {
  TYPTAP: {
    phoneNumber: "8442897968",
    email: "FNOL@TypTap.com",
    fax: "3525334073",
    customerCareEmail: "customerservice@typtap.com",
    customerCarePhone: "8442897968",
  },
  HCPCI: {
    phoneNumber: "8882105235",
    email: "FNOL@hcpci.com",
    fax: "8135142300",
    customerCareEmail: "customerservice@hcpci.com",
    customerCarePhone: "8882105235",
  },
  TAILROW: {
    phoneNumber: "8449541110",
    email: "FNOL@Tailrow.com",
    fax: "8137761970",
    customerCareEmail: "customerService@Tailrow.com",
    customerCarePhone: "8449541110",
  },
  PRAXIS: {
    phoneNumber: "8339377272",
    email: "fnol@praxisinsures.com",
    fax: "",
    customerCareEmail: "customerservice@praxisinsures.com",
    customerCarePhone: "8339377272",
  },
  CORE: {
    phoneNumber: "8447135800",
    email: "FNOL@COREIns.com",
    fax: "",
    customerCareEmail: "customerservice@coreins.com ",
    customerCarePhone: "8447135800",
  },
};

const getBrandStrings = () => {
  const companyName = getCompanyName();

  return BrandStrings[companyName];
};

const getBrandContacts = () => {
  const companyName = getCompanyName();

  return BrandContacts[companyName];
};

const { messages, labelText, description, urls, drawerData } =
  getBrandStrings();
const { phoneNumber, email, fax, customerCareEmail, customerCarePhone } =
  getBrandContacts();

export {
  messages,
  labelText,
  description,
  urls,
  phoneNumber,
  email,
  fax,
  drawerData,
  customerCareEmail,
  customerCarePhone,
  FaqJsonData
};
